import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { copy } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = `${process.env.PUBLIC_URL}/hero.jpg`;
const INSTAGRAM_URL = "https://www.instagram.com/use__carmo/";

const EASE = [0.16, 1, 0.3, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

export default function LandingOverlay() {
  const [lang, setLang] = useState("pt");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const t = copy[lang];

  const bgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: bgRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t.invalid);
      return;
    }
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        lang,
      });
      setStatus(data.status === "exists" ? "exists" : "ok");
      if (data.status !== "exists") toast(t.successTitle);
    } catch {
      setStatus("idle");
      setError(t.error);
    }
  };

  return (
    <div
      data-testid="landing-page"
      ref={bgRef}
      className="relative min-h-screen overflow-hidden bg-[#0A0A0A] text-[#0A0A0A]"
    >
      <div className="grain-overlay" aria-hidden="true" />

      {/* Full-bleed model photo */}
      <motion.img
        data-testid="hero-image"
        src={HERO_IMG}
        alt="Editorial CARMO — moda feminina"
        className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
        style={{ y: bgY, scale: bgScale }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
      />

      {/* Ivory veil for legibility — stronger where the text sits */}
      <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(250,249,246,0.92)_0%,rgba(250,249,246,0.35)_55%,rgba(250,249,246,0)_80%)] lg:bg-[linear-gradient(90deg,rgba(250,249,246,0.95)_0%,rgba(250,249,246,0.55)_45%,rgba(250,249,246,0)_75%)]" />

      {/* Transparent CARMO panel over the photo */}
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-xl flex-col items-center justify-between px-8 pb-10 pt-14 text-center lg:ml-[6%] lg:mr-auto lg:px-0 lg:pb-12 lg:pt-16">
        <div className="absolute right-6 top-6 flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] lg:right-0">
          <button
            data-testid="lang-toggle-pt"
            onClick={() => setLang("pt")}
            className={`px-2 py-1 transition-colors duration-300 ${
              lang === "pt" ? "text-[#0A0A0A]" : "text-[#0A0A0A]/45 hover:text-[#0A0A0A]/80"
            }`}
          >
            PT
          </button>
          <span className="text-[#0A0A0A]/30">/</span>
          <button
            data-testid="lang-toggle-en"
            onClick={() => setLang("en")}
            className={`px-2 py-1 transition-colors duration-300 ${
              lang === "en" ? "text-[#0A0A0A]" : "text-[#0A0A0A]/45 hover:text-[#0A0A0A]/80"
            }`}
          >
            EN
          </button>
        </div>

        <div>
          <h1 className="font-['Italiana',serif] leading-none tracking-tight">
            <RevealLine delay={0.4} className="text-[20vw] sm:text-[15vw] lg:text-[5.5rem] xl:text-[7rem]">
              <span data-testid="logo-carmo">CARMO</span>
            </RevealLine>
          </h1>
          <RevealLine delay={0.65}>
            <p
              data-testid="brand-eyebrow"
              className="mt-3 text-xs uppercase tracking-[0.55em]"
            >
              {t.eyebrow}
            </p>
          </RevealLine>
        </div>

        <div className="flex w-full flex-col items-center">
          <RevealLine delay={0.9}>
            <p
              data-testid="tagline"
              className="font-['Cormorant_Garamond',serif] text-xl italic text-[#0A0A0A]/90 sm:text-2xl"
            >
              {t.tagline}
            </p>
          </RevealLine>

          <FadeIn delay={1.1} className="mt-10 lg:mt-14">
            <p data-testid="coming-soon-label" className="text-sm uppercase tracking-[0.45em]">
              Coming Soon
            </p>
            <p className="mt-2 text-sm uppercase tracking-[0.45em]">2026</p>
          </FadeIn>

          <FadeIn delay={1.3} className="mt-10 w-full max-w-sm lg:mt-14">
            {status === "ok" || status === "exists" ? (
              <motion.div
                data-testid="subscribe-success"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <p className="font-['Cormorant_Garamond',serif] text-2xl">
                  {status === "ok" ? t.successTitle : t.alreadyTitle}
                </p>
                <p className="mt-3 text-sm font-light text-[#0A0A0A]/70">
                  {status === "ok" ? t.successText : t.alreadyText}
                </p>
              </motion.div>
            ) : (
              <form
                data-testid="subscribe-form"
                onSubmit={submit}
                noValidate
                className="flex items-end justify-center gap-5"
              >
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={lang === "pt" ? "E-mail" : "Email"}
                  className="w-full border-b border-[#0A0A0A] bg-transparent pb-2 text-sm font-light tracking-wide outline-none placeholder:text-[#0A0A0A]/45 focus:border-[#A87A63]"
                />
                <button
                  data-testid="notify-me-button"
                  type="submit"
                  disabled={status === "sending"}
                  className="shrink-0 border border-[#0A0A0A] bg-[#FAF9F6]/40 px-7 py-2.5 text-xs tracking-[0.08em] text-[#0A0A0A] backdrop-blur-sm transition-colors duration-300 hover:bg-[#0A0A0A] hover:text-[#FAF9F6] disabled:opacity-60"
                >
                  {status === "sending" ? t.sending : t.button}
                </button>
              </form>
            )}
            {error && (
              <motion.p
                data-testid="form-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 text-xs tracking-wide text-[#A87A63]"
              >
                {error}
              </motion.p>
            )}
          </FadeIn>
        </div>

        <FadeIn delay={1.55}>
          <a
            data-testid="instagram-link"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram @use__carmo"
            className="inline-block text-[#0A0A0A] transition-transform duration-300 hover:scale-110"
          >
            <Instagram size={20} strokeWidth={1.5} />
          </a>
        </FadeIn>

        <a
          data-testid="admin-link"
          href="/admin"
          className="absolute bottom-4 right-6 text-[9px] uppercase tracking-[0.3em] text-[#0A0A0A]/40 transition-colors duration-300 hover:text-[#0A0A0A] lg:right-0"
        >
          {t.admin}
        </a>
      </div>
    </div>
  );
}
